import { EmptyView, ErrorView } from '@components/PlaceholderView';
import pathnames from '@src/app/routes/pathnames';
import { useNotificationContext } from '@src/context/NotificationContext';
import React, { CSSProperties, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import NotificationItem from './NotificationItem';
import NotificationItemSkeleton from './NotificationItemSkeleton';

export interface PopupProps {
  styles?: CSSProperties;
  getFloatingProps?: (
    userProps?: React.HTMLProps<HTMLElement> | undefined,
  ) => Record<string, unknown>;
  handleHidePopup: () => void;
}
const Popup = forwardRef<HTMLDivElement, PopupProps>(
  ({ styles, getFloatingProps, handleHidePopup }, ref) => {
    const { t } = useTranslation();
    const { notificationList, total, onNotificationClick, notificationQuery } =
      useNotificationContext();
    const history = useHistory();

    const notificationListToShow = notificationList.slice(0, 3);
    const moreNotifCount = total - 3;

    const renderContent = () => {
      if (notificationQuery.isError) {
        return (
          <ErrorView
            retry={notificationQuery.refetch}
            containerClassName="flex-1 p-5"
            iconContainerClassName="sm:w-20 sm:h-20"
            iconClassName="sm:h-[48px] sm:w-[48px]"
            mainTextClassName="sm:text-base sm:mb-2"
            subTextClassName="sm:text-sm sm:mb-4"
            actionTextClassName="mb-5"
          />
        );
      }
      if (!total && !notificationQuery.isLoading) {
        return (
          <EmptyView
            mainText={t('notification.empty.mainText')}
            subText={t('notification.empty.subText')}
            containerClassName="flex-1 py-5"
            iconContainerClassName="sm:w-20 sm:h-20"
            iconClassName="sm:h-[48px] sm:w-[48px]"
            mainTextClassName="sm:text-base sm:mb-2"
            subTextClassName="sm:text-sm sm:mb-5"
          />
        );
      }

      return (
        <>
          {notificationQuery.isLoading
            ? Array.from(Array(3).keys()).map(value => (
                <NotificationItemSkeleton key={value} />
              ))
            : notificationListToShow.map(
                ({ id, title, read, description, code, metadata }) => {
                  const onClick = () => {
                    handleHidePopup();
                    onNotificationClick({
                      notificationId: id,
                      notificationCode: code,
                      isRead: read,
                      metadata,
                    });
                  };

                  return (
                    <NotificationItem
                      key={id}
                      description={description}
                      title={title}
                      isRead={read}
                      onClick={onClick}
                    />
                  );
                },
              )}

          <button
            data-testid="see-more-button"
            onClick={() => {
              handleHidePopup();
              history.push(pathnames.notification());
            }}
            className="flex items-center justify-center w-full text-sm font-semibold text-primary-500 py-7 px-10"
          >
            {moreNotifCount > 0 && moreNotifCount} More{' '}
            {moreNotifCount > 1 ? 'notifications' : 'notification'}
          </button>
        </>
      );
    };
    return (
      <div
        style={styles}
        ref={ref}
        className="bg-white border border-neutral-40 rounded-lg shadow-lg w-full sm:max-w-[430px] pt-5"
        {...getFloatingProps?.()}
      >
        {renderContent()}
      </div>
    );
  },
);

export default Popup;
